import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, Dialog, Divider, IconButton, Stack, Typography } from '@mui/material';
import CalendarSvg from '../utility/Svg/CalendarSvg';
import CustomerLicencePlate from '../utility/Svg/CustomerLicencePlate';
import DocumentNumberSvg from '../utility/Svg/DocumentNumberSvg';
import UserSvg from '../utility/Svg/UserSvg';

/** Wording + draft detail route per document type. */
export const OPEN_DRAFT_DOCUMENT = {
    estimate: { label: 'estimate', detailPath: (id) => `/estimates/draft/${id}` },
    invoice: { label: 'invoice', detailPath: (id) => `/invoice/draft/${id}` },
    workOrder: { label: 'work order', detailPath: (id) => `/work-orders/draft/${id}` },
};

// Header and actions are Stacks, not Boxes: App.css strips borders from every `.MuiBox-root` inside a modal.
const styles = {
    paper: {
        width: 'calc(100% - 32px)',
        maxWidth: '613px',
        margin: '16px',
        borderRadius: '8px',
        overflow: 'hidden',
        boxShadow: '0px 1px 3px 0px rgba(0,0,0,0.12), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 2px 1px -1px rgba(0,0,0,0.2)',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        height: '64px',
        flexShrink: 0,
        padding: '0 16px',
        backgroundColor: '#fff',
        borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
    },
    title: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        fontSize: '20px',
        fontWeight: 500,
        lineHeight: 1.6,
        letterSpacing: '0.15px',
        color: 'rgba(0, 0, 0, 0.87)',
    },
    closeButton: {
        padding: '8px',
    },
    closeIcon: {
        fontSize: '35px',
        color: 'rgba(0, 0, 0, 0.87)',
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        padding: '16px',
    },
    message: {
        fontSize: '16px',
        fontWeight: 400,
        lineHeight: '24px',
        letterSpacing: '0.15px',
        color: '#000',
    },
    divider: {
        borderColor: 'rgba(0, 0, 0, 0.1)',
    },
    details: {
        display: 'flex',
        gap: '20px',
    },
    detailColumn: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        flex: '1 0 0',
        minWidth: 0,
    },
    detailRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        minWidth: 0,
    },
    detailIcon: {
        display: 'flex',
        flexShrink: 0,
    },
    detailText: {
        fontSize: '16px',
        fontWeight: 400,
        lineHeight: 1.2,
        letterSpacing: '0.15px',
        color: 'rgba(0, 0, 0, 0.87)',
    },
    actions: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: '8px',
        padding: '16px',
        borderTop: '1px solid rgba(0, 0, 0, 0.1)',
    },
};

const DetailRow = ({ icon, label }) => (
    <Box sx={styles.detailRow}>
        <Box sx={styles.detailIcon}>{icon}</Box>
        <Typography noWrap title={label} sx={styles.detailText}>
            {label || '—'}
        </Typography>
    </Box>
);

/**
 * "Open draft already exists" alert — shown while creating a new estimate, invoice or work order
 * when the selected licence plate already has a draft of that document type.
 *
 * `draft`: { customerName, documentNumber, vehicle, date }
 * Create new and the close icon both call `onClose` — the user keeps editing.
 */
const OpenDraftExistsDialog = ({ open = false, documentType, draft, onClose, onViewDraft }) => {
    const { label } = OPEN_DRAFT_DOCUMENT[documentType] || OPEN_DRAFT_DOCUMENT.invoice;

    return (
        <Dialog
            open={open}
            onClose={(event, reason) => {
                if (reason !== 'backdropClick') onClose?.();
            }}
            aria-labelledby="open-draft-exists-title"
            aria-describedby="open-draft-exists-description"
            // No MUI breakpoint cap (sm = 600px) — the paper's own 613px max width applies.
            maxWidth={false}
            PaperProps={{ sx: styles.paper }}
        >
            <Stack direction="row" sx={styles.header}>
                <Typography id="open-draft-exists-title" component="h2" sx={styles.title}>
                    <span>⚠️</span>
                    Open draft already exists
                </Typography>
                <IconButton onClick={() => onClose?.()} aria-label="close" sx={styles.closeButton}>
                    <CloseIcon sx={styles.closeIcon} />
                </IconButton>
            </Stack>

            <Box sx={styles.content}>
                <Typography id="open-draft-exists-description" sx={styles.message}>
                    An existing {label} in draft status exists for {draft?.customerName || 'this customer'}. Open draft or make a new {label}?
                </Typography>

                <Divider sx={styles.divider} />

                <Box sx={styles.details}>
                    <Box sx={styles.detailColumn}>
                        <DetailRow icon={<UserSvg />} label={draft?.customerName} />
                        <DetailRow icon={<CustomerLicencePlate />} label={draft?.vehicle} />
                    </Box>
                    <Box sx={styles.detailColumn}>
                        <DetailRow icon={<DocumentNumberSvg />} label={draft?.documentNumber} />
                        <DetailRow icon={<CalendarSvg />} label={draft?.date} />
                    </Box>
                </Box>
            </Box>

            <Stack direction="row" sx={styles.actions}>
                <Button onClick={() => onViewDraft?.()} variant="outlined" color="primary" size="large">
                    View Draft
                </Button>
                <Button onClick={() => onClose?.()} variant="contained" color="primary" size="large">
                    Create new
                </Button>
            </Stack>
        </Dialog>
    );
};

export default OpenDraftExistsDialog;
